import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AdminGoal } from '../lib/admin';
import { GOAL_STATUS_TONE, goalQuietLabel, goalUnmeasuredLabel } from '../lib/goal-view';
import { createMutation } from '../lib/mutation';
import { Button, buttonClass } from './ui/Button';

// One goal in the monitor. Presenter only — every number is the API's.
//
// The rename controls are the SHARED primitives — `.btn` / `.btn--sm` /
// `.btn--ghost` and `.input` (spec §3.1, §3.10) — not bespoke CSS. They already
// carry hover, `:active`, the focus ring, the disabled treatment and their motion
// gated behind `prefers-reduced-motion`; the hand-rolled versions this replaces
// had none of that and drifted from every other control on the console.
//
// A one-item goal (`measurable: false`, which is every epic) gets NO progress
// bar: an epic is a whole body of work tracked as a single issue, so `0/1 = 0%`
// renders it as untouched. That is the flattering-number defect pointed the
// other way, and a bar is the one control that cannot say "this does not apply".
//
// `measurable: false` is `total > 1` failing, which is TWO cases, and they take
// different words. A milestone with nothing filed against it read "Tracked as
// one issue" on the live board — a statement about the goal that was false, on
// the page whose whole claim is that its statements are not. One item means the
// work exists and is not divisible into a percentage; zero means nobody has
// filed it, which is the difference between "in flight" and "not begun".

// ponytail: the copy's locale (`t.locale`); a `lib/time.ts` number seam would retire it.
const NUM = new Intl.NumberFormat(t.locale);

export function GoalRow(props: {
  goal: AdminGoal;
  /** Absent ⇒ the row is read-only, which is what a caller without the write scope gets. */
  onRename?: (id: string, title: string) => Promise<void>;
}): JSX.Element {
  const quiet = () => goalQuietLabel(props.goal);
  const [editing, setEditing] = createSignal(false);
  const [draft, setDraft] = createSignal(props.goal.title);

  // `errorReason` is the same rule this row spelled inline: `ApiError.message` is
  // the transport line (`PATCH /v1/… → 422`), while the server's own sentence is
  // in `detail`, and it is the half that tells an operator WHICH title was
  // refused. It additionally surfaces a non-API failure's message, where the
  // inline version collapsed a dead network into the generic copy.
  const rename = createMutation({
    call: (next: string) => props.onRename?.(props.goal.id, next) ?? Promise.resolve(),
    fallback: t('goals.rename_failed'),
    onSuccess: () => setEditing(false),
  });
  const saving = rename.pending;
  const error = rename.error;

  const commit = (): void => {
    const next = draft().trim();
    // No empty-title branch here, and that is deliberate: the submit button is
    // `disabled` while `draft().trim()` is empty, so a blank title reaches
    // neither Save nor Enter — HTML performs no implicit submission through a
    // disabled submit button. A second guard would be a second seam for one
    // invariant, and an unreachable one, which reads to the next person as a
    // case that CAN happen.
    //
    // `next === ''` survives only as the guard for that assumption: if the
    // disabled attribute is ever removed, this returns rather than PATCHing an
    // empty title at GitHub.
    if (next === '') return;
    // An unchanged title IS nothing-happened, so it closes silently and writes
    // nothing: GitHub would accept it and the audit trail would carry a rename
    // that renamed nothing.
    if (next === props.goal.title) {
      setEditing(false);
      return;
    }
    void rename.run(next);
  };
  return (
    <li class={`goal ${GOAL_STATUS_TONE[props.goal.status]}`}>
      <div class="goal__head">
        <Show
          when={editing()}
          fallback={
            <a class="goal__title" href={props.goal.url} target="_blank" rel="noreferrer">
              {props.goal.title}
            </a>
          }
        >
          {/* A form, so Enter submits and Escape is the browser's own affordance
              — a rename that only commits on a mouse click is one a keyboard
              user cannot finish. */}
          <form
            class="goal__rename-form"
            onSubmit={(e) => {
              e.preventDefault();
              commit();
            }}
          >
            <input
              class="input goal__rename-input"
              value={draft()}
              disabled={saving()}
              aria-label={t('goals.rename_label', { title: props.goal.title })}
              onInput={(e) => setDraft(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditing(false);
              }}
              ref={(el) => queueMicrotask(() => el.focus())}
            />
            <button
              type="submit"
              class={buttonClass({ size: 'sm' })}
              disabled={saving() || !draft().trim()}
            >
              {saving() ? t('goals.renaming') : t('goals.rename_save')}
            </button>
            <Button variant="ghost" size="sm" disabled={saving()} onClick={() => setEditing(false)}>
              {t('goals.rename_cancel')}
            </Button>
          </form>
        </Show>
        <span class="goal__status">{t(`goals.status.${props.goal.status}`)}</span>

        {/* LAST in the row, after the status, so it lands in the same column on
            every goal — a control that moves with the length of a title is one
            you have to hunt for. Always rendered, never revealed on hover: an
            action you cannot see is an action nobody finds. */}
        <Show when={props.onRename !== undefined && !editing()}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setDraft(props.goal.title);
              rename.clearError();
              setEditing(true);
            }}
          >
            {t('goals.rename')}
          </Button>
        </Show>
      </div>

      {/* A failed rename says so HERE, beside the row it failed on. The write
          goes to GitHub, so "it did not save" and "it saved and the board is
          stale" are different states, and a silent failure would leave a reader
          believing the first while looking at the second. */}
      <Show when={error()}>
        {(msg) => (
          <p class="goal__rename-error" role="alert">
            {msg()}
          </p>
        )}
      </Show>

      <Show
        when={props.goal.measurable}
        fallback={
          <p class="goal__meta">
            {goalUnmeasuredLabel(props.goal)} · {t(`goals.kind.${props.goal.kind}`)}
          </p>
        }
      >
        <div
          class="progress goal__progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={props.goal.percent}
          // Named by the goal, so it reads "<title>, 97%"; the value is the number.
          aria-label={props.goal.title}
        >
          <div class="progress__indicator" style={{ width: `${props.goal.percent}%` }} />
        </div>
        <p class="goal__meta">
          <strong>{props.goal.percent}%</strong> ·{' '}
          {t('goals.items_done', {
            closed: NUM.format(props.goal.closed),
            total: NUM.format(props.goal.total),
          })}
        </p>
      </Show>

      <p class="goal__meta goal__meta--facts">
        <Show when={quiet()}>{(text) => <span class="goal__fact">{text()}</span>}</Show>
        <Show when={props.goal.blocked > 0}>
          <span class="goal__fact goal__fact--warn">
            {t('goals.blocked_count', { count: NUM.format(props.goal.blocked) })}
          </span>
        </Show>
        <Show when={props.goal.overdue}>
          <span class="goal__fact goal__fact--warn">{t('goals.overdue')}</span>
        </Show>
      </p>
    </li>
  );
}
